import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { wishlistAPI } from '../lib/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const WishlistContext = createContext({});

export function WishlistProvider({ children }) {
    const { isAuthenticated } = useAuth();
    const [wishlistIds, setWishlistIds] = useState(new Set());
    const [loading, setLoading] = useState(false);

    const fetchWishlistIds = useCallback(async () => {
        if (!isAuthenticated) {
            setWishlistIds(new Set());
            return;
        }
        try {
            const res = await wishlistAPI.getIds();
            setWishlistIds(new Set(res.data.ids || []));
        } catch {
            // silent fail
        }
    }, [isAuthenticated]);

    useEffect(() => {
        fetchWishlistIds();
    }, [fetchWishlistIds]);

    const toggleWishlist = useCallback(async (productId) => {
        if (!isAuthenticated) {
            toast.error('Jelentkezz be a mentéshez!');
            return;
        }
        const isInWishlist = wishlistIds.has(productId);
        // Optimistic update
        setWishlistIds(prev => {
            const next = new Set(prev);
            if (isInWishlist) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });

        try {
            if (isInWishlist) {
                await wishlistAPI.remove(productId);
                toast.success('Eltávolítva a mentettek közül');
            } else {
                await wishlistAPI.add(productId);
                toast.success('Mentve a kívánságlistára!');
            }
        } catch {
            // Revert on error
            setWishlistIds(prev => {
                const next = new Set(prev);
                if (isInWishlist) {
                    next.add(productId);
                } else {
                    next.delete(productId);
                }
                return next;
            });
            toast.error('Hiba történt');
        }
    }, [isAuthenticated, wishlistIds]);

    const isInWishlist = useCallback((productId) => wishlistIds.has(productId), [wishlistIds]);

    return (
        <WishlistContext.Provider value={{ wishlistIds, toggleWishlist, isInWishlist, loading, refresh: fetchWishlistIds }}>
            {children}
        </WishlistContext.Provider>
    );
}

export function useWishlist() {
    return useContext(WishlistContext);
}

export default WishlistContext;
